import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { API_URL_USERS } from '@shared/config';
import { AuthenticationService } from './authentication.service';
import { BackendUserDetails, UserDetails, UserOrganisationDetails, UserSecurityDetails } from '../auth.data';
import { INTENDED_URL } from '../auth.data';
import { UpdatePasswordInput } from 'aws-amplify/auth';

@Injectable({
  providedIn: 'root'
})
export class MockAuthenticationService implements AuthenticationService {
  private http = inject(HttpClient);
  private apiUrl = inject(API_URL_USERS);
  private router = inject(Router);
  private rendererFactory = inject(RendererFactory2);

  private renderer: Renderer2;
  constructor() {
    const rendererFactory = this.rendererFactory;

    this.renderer = rendererFactory.createRenderer(null, null);
    console.info('Mock Authentication Active');
  }

  public openCognitoLoginPage(): void {
    // Mock implementation - just navigate to callback
    this.router.navigate(['/cognito-callback']);
  }

  public async handleAuthCallbackAndRedirectToIntendedUrl(): Promise<void> {
    // Mock implementation - simulate successful auth callback
    localStorage.setItem('mock_authenticated', 'true');

    // Fetch user details from backend and store them
    await this.fetchAndStoreUserDetails();
  }

  private async fetchAndStoreUserDetails(): Promise<void> {
    const url = this.apiUrl + '/user';
    const response = await firstValueFrom(this.http.get<BackendUserDetails>(url));
    const session = {
      tokens: {
        idToken: {
          payload: {
            email: response.email
          }
        }
      }
    };
    this.storeUserSecurityDetails(session);
    this.storeUserDetails(response);
    this.redirectAfterLogin();
  }

  private redirectAfterLogin(): void {
    const returnUrl = sessionStorage.getItem(INTENDED_URL);
    sessionStorage.removeItem(INTENDED_URL);
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigate(['/']);
    }
  }

  public async getAccessTokenAsync(): Promise<string> {
    // Mock implementation returns a fake token
    return 'mock-access-token';
  }

  public isAuthenticatedQuickCheck(): boolean {
    return localStorage.getItem('mock_authenticated') === 'true';
  }

  public async isAuthenticated(): Promise<boolean> {
    return localStorage.getItem('mock_authenticated') === 'true';
  }

  storeUserSecurityDetails(session: { tokens?: { idToken?: { payload?: Record<string, unknown> } } }): void {
    const email = session.tokens?.idToken?.payload?.['email'] as string | undefined;

    const userToStore: UserSecurityDetails = {
      email: email || null,
      gravatar: email ? this.getUserGravatarPath(email) : null
    };

    localStorage.setItem('userSecurity', JSON.stringify(userToStore));
  }
  storeUserDetails(user: BackendUserDetails) {
    const uiSkinName = 'idefigo'; // once has been added to backend: userArray[0].uiSkinName.toString().toLowerCase(); then add kebab case pipe so cyclops-monitoring will work.
    this.renderer.setAttribute(document.documentElement, 'ui-skin', uiSkinName);
    localStorage.setItem('uiSkinName', uiSkinName);

    const userToStore: UserDetails = {
      displayName: user.displayName,
      firstName: user.firstName,
      lastName: user.lastName,
      locale: user.attributes?.locale,
      timezone: user.attributes?.timezone,
      description: user.displayName,
      id: 999999
    };
    const userOrganisationToStore: UserOrganisationDetails = {
      name: user.orgName,
      id: user.orgId
    };
    const userRoles = user.roles;

    localStorage.setItem('user', JSON.stringify(userToStore));
    localStorage.setItem('userOrganisation', JSON.stringify(userOrganisationToStore));
    localStorage.setItem('userRoles', JSON.stringify(userRoles));
    localStorage.setItem('baseOrgId', JSON.stringify(userOrganisationToStore.id));
    localStorage.setItem('mock_authenticated', 'true');
  }

  getUserSecurityDetails(): UserSecurityDetails {
    return JSON.parse(localStorage.getItem('userSecurity') || '{}');
  }

  getUserDetails(): UserDetails {
    return JSON.parse(localStorage.getItem('user') || '{}');
  }
  getUserOrganisationDetails(): UserOrganisationDetails {
    return JSON.parse(localStorage.getItem('userOrganisation') || '{}');
  }

  getUserGravatarPath(userEmail: string): string {
    if (!userEmail) {
      return 'https://www.gravatar.com/avatar/?d=mp';
    }
    return 'https://www.gravatar.com/avatar/ops@cortexo.com?d=mp';
  }

  hasRole(role: string): boolean {
    return this.getUserRoles().includes(role);
  }

  getUserRoles(): Array<string> {
    return JSON.parse(localStorage.getItem('userRoles') || '[]');
  }

  logout(): void {
    this.renderer.removeAttribute(document.documentElement, 'ui-skin');
    localStorage.clear();
    this.router.navigate(['/welcome']);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async changePassword({ oldPassword, newPassword }: UpdatePasswordInput): Promise<boolean> {
    return false;
  }
}
